<script lang="ts">
import type {TranslationModalData} from "@/stores/translationModal";
import {defineComponent} from "vue";
import Modal from "@/components/Modal.vue";
import Button from "@/components/Button.vue";
import {useTranslationModal} from "@/stores/translationModal";
import Input from "@/components/Input.vue";
import {getEmojiFromLanguageCode, getLanguageNameByCode} from "@/utils/CountriesUtils";
import WarningIcon from "@/assets/icons/WarningIcon.vue";
import {handleError} from "@/utils/FormatUtils";
import useVuelidate from "@vuelidate/core";
import {helpers} from "@vuelidate/validators";

export default defineComponent({
  name: 'DynamicTranslationModal',
  components: {
    WarningIcon,
    Input,
    Button,
    Modal
  },

  setup() {
    const translationStore = useTranslationModal();

    return {
      translationStore,
      v$: useVuelidate()
    }
  },

  validations() {
    return {
      localData: {
        translations: {
          $each: helpers.forEach({
            text: {
              required: helpers.withMessage('Required', (value, vm) => {
                if (this.isOptional) return true;

                const isDefaultLanguage = vm.language === 'en';

                return !isDefaultLanguage ? true : (value as string).length > 0;
              }),
            }
          }),
        }
      }
    }
  },

  props: {},

  data() {
    return {
      id: null as string | null,
      localData: null as TranslationModalData | null,

      isOptional: false
    }
  },

  computed: {
    getDefaultTranslation() {
      return this.localData?.translations?.find((t: { language: string; }) => t.language === 'en');
    },

    getOtherTranslations() {
      return this.localData?.translations?.filter((t: { language: string; }) => t.language !== 'en');
    },

    getIndexOfDefaultTranslation() {
      return this.localData?.translations.findIndex((t: { language: string; }) => t.language === 'en');
    }
  },

  watch: {},

  methods: {
    handleError,
    getLanguageNameByCode,
    getEmojiFromLanguageCode,

    openModal(id: string) {
      if (!id) {
        throw new Error("ID is required to open TranslationModal");
      }

      this.id = id;

      this.localData = (JSON.parse(JSON.stringify(this.translationStore.data)) as typeof this.localData);

      this.isOptional = this.translationStore.data!.isOptional;

      ;(this.$refs.modal as InstanceType<typeof Modal>).openModal();

      this.$nextTick(() => {
        this.updateHeightOtherTranslations();
      });
    },

    handleSave() {
      this.translationStore.data = this.localData;

      this.translationStore.commit();
      ;(this.$refs.modal as InstanceType<typeof Modal>).closeModal();
    },

    handleCancel() {
      ;(this.$refs.modal as InstanceType<typeof Modal>).manualClose();
    },

    onModalClose() {
      this.translationStore.close();
      this.handleCancel();
    },

    updateHeightOtherTranslations() {
      const defaultTranslation = (this.$refs.defaultTranslation as HTMLElement);
      const otherTranslations = (this.$refs.otherTranslations as HTMLElement);

      if (defaultTranslation && otherTranslations) {
        otherTranslations.style.maxHeight = 'none';

        const bodyHeight = (this.$refs.modal as InstanceType<typeof Modal>).getBodyHeight();
        const defaultHeight = defaultTranslation.offsetHeight;

        const maxHeight = bodyHeight - defaultHeight;

        otherTranslations.style.maxHeight = `${maxHeight}px`;
      }
    }
  },

  mounted() {
    this.v$.$touch();

    this.updateHeightOtherTranslations();

    window.addEventListener('resize', this.updateHeightOtherTranslations);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateHeightOtherTranslations);
  }
})
</script>

<template>
  <Modal
      ref="modal"
      title="Label Translation"
      size="md"
      auto-height
      body-class="overflow-hidden pt-0 pb-0"
      @close="onModalClose"
  >
    <template #body>
      <div class="default-translation" ref="defaultTranslation">
        <div class="row mx-0 my-0 gx-1">
          <div class="col-1 d-flex align-items-center justify-content-center fs-6 mt-3">
            <i :class="getEmojiFromLanguageCode(getDefaultTranslation!.language)"/>
          </div>
          <Input
              :label="getLanguageNameByCode(getDefaultTranslation!.language)"
              v-model:value="getDefaultTranslation!.text"
              :text-area="localData!.isTextArea"
              :error-message="handleError(v$.localData.translations.$each.$response.$errors[getIndexOfDefaultTranslation!].text)"
              class="col-11"
          />
        </div>
      </div>

      <div class="other-translations flex-grow-1 overflow-y-auto py-2" ref="otherTranslations">
        <div class="row mx-0 my-0 gx-1 gy-2">
          <div
              class="col-12"
              v-for="(translation, index) in getOtherTranslations"
              :key="index"
          >
            <div class="row mx-0 gx-1">
              <div class="col-1 d-flex align-items-center justify-content-center fs-6 mt-3">
                <i :class="getEmojiFromLanguageCode(translation.language)"/>
              </div>
              <Input
                  :label="getLanguageNameByCode(translation.language)"
                  v-model:value="translation.text"
                  class="col-11"
                  :text-area="localData!.isTextArea"
                  :input-class="translation.is_fallback ? 'input-warning' : ''"
              >
                <template #icons>
                  <WarningIcon class="warning-icon me-2" :type="2"
                               v-if="translation.is_fallback"/>
                </template>
              </Input>
            </div>
          </div>
        </div>
      </div>
    </template>


    <template #footer>
      <Button
          type="blue"
          class="col-12"
          :callback="handleSave"
      >
        Save
      </Button>
      <Button
          type="gray"
          class="col-12"
          :callback="handleCancel"
      >
        Cancel
      </Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.default-translation {
  padding: 20px 0;

  border-bottom: 1px solid rgba(194, 232, 255, 0.1);
}
</style>
